/**
 * Live signal ticker — Website Build Brief v2 §5.
 *
 * "Items load from a simple JSON array so Hiba can rotate real anonymized signals
 *  weekly without a deploy."
 *
 * Items come from /ticker.json, a plain static file. Editing it is a content
 * change, not a code change — no touching TypeScript, no build config. The markup
 * already in index.html is the fallback if the fetch fails or JS is off.
 */

interface TickerItem {
  /** The signal line, e.g. "SERIES B $40M — logistics software co." */
  text: string;
  /** Optional: renders in gold rather than the default grey. Scarcity only. */
  gold?: boolean;
  /** Optional: prefixes the pulsing live dot. */
  live?: boolean;
}

function render(items: TickerItem[]): string {
  return items
    .map(item => {
      const cls = item.gold ? ' class="gold"' : "";
      const dot = item.live ? '<span class="dot"></span>' : "";
      // textContent-style escaping — ticker.json is edited by hand, so never
      // trust it to be HTML-safe.
      const div = document.createElement("div");
      div.textContent = item.text;
      return `<span${cls}>${dot}${div.innerHTML}</span>`;
    })
    .join("");
}

export async function initTicker(): Promise<void> {
  const inner = document.querySelector<HTMLElement>("[data-ticker]");
  if (!inner) return;

  try {
    const res = await fetch("/ticker.json", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;
    const items: unknown = await res.json();
    if (!Array.isArray(items) || !items.length) return;

    const valid = items.filter(
      (i): i is TickerItem =>
        typeof i === "object" && i !== null && typeof i.text === "string"
    );
    if (!valid.length) return;

    // Duplicated once: the CSS marquee translates -50%, so two copies make the
    // loop seamless. Same trick the mock uses with its hardcoded list.
    inner.innerHTML = render(valid) + render(valid);
  } catch {
    // Keep the markup already in the page.
  }
}
